import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, Edit, Sun, Moon } from "lucide-react";
import { API_URL } from "@/lib/api";

const API = API_URL;

export default function Settings({ currentStrategy, mode }) {
  const { theme, setTheme } = useTheme();
  const [strategies, setStrategies] = useState([]);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    currency: "USD",
    initial_capital: 0,
  });

  const loadStrategies = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/strategies?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStrategies(response.data);
    } catch (error) {
      console.error("Error loading strategies:", error);
    }
  }, [mode]);

  useEffect(() => {
    loadStrategies();
  }, [currentStrategy, loadStrategies]);

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setFormData({
      name: strategy.name,
      currency: strategy.currency,
      initial_capital: strategy.initial_capital,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/strategies/${editingStrategy.id}?mode=${mode}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Strategy updated successfully!");
      setEditingStrategy(null);
      loadStrategies();
    } catch (error) {
      toast.error("Error updating strategy");
    }
  };

  const handleReset = async (strategyId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/strategies/${strategyId}/reset?mode=${mode}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Strategy data reset successfully!");
    } catch (error) {
      toast.error("Error resetting data");
    }
  };

  const handleDelete = async (strategyId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/strategies/${strategyId}?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Strategy deleted successfully!");
      loadStrategies();
    } catch (error) {
      toast.error("Error deleting strategy");
    }
  };

  return (
    <div className="p-4 md:p-6" data-testid="settings-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Gestione Strategie</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Gestisci le tue strategie di trading
          </p>
        </div>

        {/* Theme Toggle */}
        <Card className="mb-6 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Appearance</CardTitle>
            <CardDescription>Switch between light and dark mode</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {theme === "dark" ? (
                  <Moon className="h-5 w-5 text-primary" />
                ) : (
                  <Sun className="h-5 w-5 text-primary" />
                )}
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-muted-foreground">
                    {theme === "dark" ? "Dark theme active" : "Light theme active"}
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === "dark"}
                onCheckedChange={(checked) => {
                  setTheme(checked ? "dark" : "light");
                  toast.success(`Switched to ${checked ? "dark" : "light"} mode`);
                }}
                data-testid="theme-toggle"
              />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {strategies.map((strategy, index) => (
            <Card key={strategy.id} className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`strategy-card-${index}`}>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                  <div>
                    <CardTitle data-testid={`strategy-name-${index}`}>{strategy.name}</CardTitle>
                    <CardDescription className="text-sm">
                      Currency: {strategy.currency} | Initial Capital:{" "}
                      {strategy.initial_capital.toLocaleString()} {strategy.currency}
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(strategy)}
                      data-testid={`edit-strategy-button-${index}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" data-testid={`delete-strategy-button-${index}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid={`delete-strategy-dialog-${index}`}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action will permanently delete the strategy and all associated trades.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(strategy.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-button"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="rounded-full"
                      data-testid={`reset-strategy-button-${index}`}
                    >
                      Reset Strategy Data
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`reset-strategy-dialog-${index}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset data?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action will delete all trades for this strategy. The strategy itself will remain active.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-reset-button">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleReset(strategy.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        data-testid="confirm-reset-button"
                      >
                        Reset
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          ))}
        </div>

        {editingStrategy && (
          <Card className="mt-6 shadow-lg" data-testid="edit-strategy-card">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Edit Strategy</CardTitle>
              <CardDescription>Update strategy details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Strategy Name</Label>
                  <Input
                    id="edit-name"
                    data-testid="edit-strategy-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-currency">Currency</Label>
                  <Input
                    id="edit-currency"
                    data-testid="edit-strategy-currency-input"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                    placeholder="USD, EUR, GBP..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-capital">Initial Capital</Label>
                  <Input
                    id="edit-capital"
                    data-testid="edit-strategy-capital-input"
                    type="number"
                    step="0.01"
                    value={formData.initial_capital}
                    onChange={(e) =>
                      setFormData({ ...formData, initial_capital: parseFloat(e.target.value) })
                    }
                    required
                  />
                </div>
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                  <Button type="submit" className="rounded-full" data-testid="submit-edit-strategy-button">
                    Update Strategy
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setEditingStrategy(null)}
                    data-testid="cancel-edit-strategy-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
